import * as angular from 'angular';
import * as moment from 'moment';
import * as d3 from 'd3';

import { AlarmsTableWidget } from './alarmsTable.widget';
import {
    IAlarmService,
    IDateRangeService,
    IExportToCsvService,
    IFormatCsvService,
    IFormatNumberHelper,
    IGroupSelectionService,
    IsparklineHelperService,
    ITranslatedLoggerService,
    IwidgetIntervalHelperService
} from '../../services';
import { refreshClick, guidEmpty, getUnitName } from '../../common';
import { Row } from './Row';
import { Alarm, AlarmList, ComponentDefinition, CsvFormatConfiguration, ILineChartData, InformationIdValue, RefreshClickData } from '../../models';
import { HeadingDefinition } from '../logbookTable/HeadingDefinition';
import { getAlarmsTableHeadings } from './getAlarmsTableHeadings';
import { formatValueForCsv } from '../../services/data/formatValueForCsv';
import { AlarmsTableConfiguration } from './alarmsTable.configuration';
import { RoundingHelper } from '../../wtFramework';

const defaultTrendSize = {
    width: 170,
    height: 20
};

class AlarmsTableController implements angular.IComponentController {
    alarmDefinitions: AlarmList;
    beginDate: string;
    boundData: { guid: string; results: Row[] };
    clicked: ILineChartData;
    endDate: string;
    hasGroupName: boolean = false;
    hasMultipleAlarmsWithSameSeverity: boolean = false;
    headings: HeadingDefinition[][];
    identifierGuidList: string[];
    isEditMode: boolean = false;
    isLoading: boolean;
    item: AlarmsTableWidget;
    items: Row[] = [];
    nbOfColumn: number;
    selectedItem: Row;
    trendHeight: number;
    trendWidth: number;

    readonly ratio: number;

    private _originalItems: Row[];
    private _levelScale = d3.scale.ordinal().domain(['critical', 'warning', 'information']).range([2, 1, 0]);

    static $inject = [
        '$scope',
        '$q',
        'dateRangeService',
        'widgetIntervalHelperService',
        'alarmService',
        'groupSelectionService',
        'sparklineHelperService',
        'exportToCsvService',
        'formatCsvService',
        'formatNumberHelper',
        'translatedLoggerService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _dateRangeService: IDateRangeService,
        private _intervalHelper: IwidgetIntervalHelperService,
        private _alarmService: IAlarmService,
        private _groupSelectionService: IGroupSelectionService,
        private _sparklineHelper: IsparklineHelperService,
        private _exportToCsvService: IExportToCsvService,
        private _formatCsvService: IFormatCsvService<Row>,
        private _formatNumberHelper: IFormatNumberHelper,
        private _loggerService: ITranslatedLoggerService
    ) {}

    $onInit() {
        this.trendHeight = 20;
        this.trendWidth = 650;

        this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
            this.refresh(data.force);
        });

        this._$scope.$watch(
            () => this.clicked,
            (newValue) => {
                if (newValue) {
                    this.items = newValue.isSelected ? this._originalItems.filter((d) => d.level === this._levelScale(newValue.classed)) : this._originalItems;
                }
            }
        );

        this._$scope.$watch(
            () => this.identifierGuidList,
            () => {
                if (this.identifierGuidList) {
                    this.getAlarms(this.identifierGuidList)
                        .then((alarmDefinitions) => {
                            this.alarmDefinitions = alarmDefinitions;
                            this.items = this.addParametersToAlarms(this.items, this.alarmDefinitions);
                        })
                        .finally(() => (this.isLoading = false));
                }
            }
        );

        this._$scope.$watch(
            () => this.item.configuration.showIdentifier,
            (newValue) => {
                if (newValue === undefined) {
                    this.item.configuration.showIdentifier = true;
                }
            }
        );

        if (!this.boundData) {
            this.refresh();
        }

        this._$scope.$watch(
            () => this.item.configuration,
            () => {
                const { configuration } = this.item;
                const headings: HeadingDefinition[] = getAlarmsTableHeadings(configuration);
                this.headings = [headings];
                this.computeSparkleWidth();
                if (!this.boundData) {
                    this.refresh(true);
                }
                const { nbOfColumn } = this.getCsvFormatConfiguration(configuration);
                this.nbOfColumn = nbOfColumn;
            },
            true
        );
        if (this.item) {
            this.item.exportToCsv = this.exportToCsv.bind(this);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['boundData']) {
            const data = changesObj['boundData'].currentValue;
            if (data && data.results) {
                const defaultInterval = this._intervalHelper.getCurrentInterval();
                const beginDate = defaultInterval.beginDate;
                const endDate = defaultInterval.endDate;
                this.beginDate = beginDate;
                this.endDate = endDate;
                this.processDateForSparkLine(data.results, beginDate, endDate);
                this.items = data.results.map((result) => {
                    const unitName = getUnitName(result.unitName, result.valueType);
                    result.unitName = unitName;
                    return result;
                });
                this._originalItems = data.results;
                this.identifierGuidList = data.results.map((i) => i.identifierGuid);
            }
            const { nbOfColumn } = this.getCsvFormatConfiguration(this.item?.configuration);
            this.nbOfColumn = nbOfColumn;
        }
    }

    private computeSparkleWidth() {
        this.trendWidth = 170;
        this.processDateForSparkLine(this.items, this.beginDate, this.endDate);
    }

    private refresh(force: boolean = false) {
        if (this._dateRangeService.initialized()) {
            this.isLoading = true;

            const referenceGroup = this._groupSelectionService.selectedGroup();
            const defaultInterval = this._intervalHelper.getCurrentInterval();

            const interval = this._intervalHelper.getInterval(defaultInterval.beginDate, defaultInterval.endDate, {
                minPeriod: this.item.configuration.minPeriod,
                maxPeriod: this.item.configuration.maxPeriod,
                isEndDateIncluded: false
            });
            const beginDate = interval.beginMoment.toISOString();
            const endDate = interval.endMoment.toISOString();
            this.beginDate = beginDate;
            this.endDate = endDate;
            this.item.configuration.beginDateTime = beginDate;
            this.item.configuration.endDateTime = endDate;
            // auto-deselect isBoundToGroup if there is no reference group
            if (referenceGroup === null || referenceGroup === undefined) {
                this.item.configuration.filters.isBoundToGroup = false;
            }
            this.item.configuration.referenceGroup = referenceGroup ? referenceGroup.guid : guidEmpty;
            this.processDateForSparkLine(this.items, beginDate, endDate);
            this._originalItems = this.items;
            this.identifierGuidList = this.items.map((i) => i.identifierGuid);
        }
    }

    private processDateForSparkLine(data: Row[], beginDate: string, endDate: string) {
        const ratio = this.ratio || 1;
        this.trendWidth = defaultTrendSize.width * ratio;
        this.trendHeight = defaultTrendSize.height * ratio;
        data.forEach((d) => {
            const numbers = d3.extent(d.enumerable, (e: InformationIdValue) => e.value);
            const roundingHelper = new RoundingHelper();
            const manualMinMax = roundingHelper.round(d3.min(numbers), d3.max(numbers));
            const min = Math.min(d3.min(d.alarmValues) ?? manualMinMax.min, numbers[0]);
            const max = Math.max(d3.max(d.alarmValues) ?? manualMinMax.max, numbers[1]);
            this._sparklineHelper
                .resize(this.trendWidth, this.trendHeight)
                .data(d.enumerable)
                .yDomain([min, max])
                .xDomain([new Date(beginDate), new Date(endDate)]);
            d.path = this._sparklineHelper.path(d.enumerable);
            d.ys = d.alarmValues.map((a) => this._sparklineHelper.scaleY(a));
            d.domain = this._sparklineHelper.xDomain();
            d.yDomain = this._sparklineHelper.yDomain();
        });
    }

    exportToCsv(): angular.IPromise<[]> {
        const { configuration } = this.getCsvFormatConfiguration(this.item.configuration);
        const dataTable = this._formatCsvService.getFormattedCsv(this.items, configuration);
        this._exportToCsvService.getCsvFromData(
            dataTable,
            `Alarms_${this.item.name}_${moment(this._dateRangeService.beginDate() as string).format('YYYYMMDD_HHmm')}.csv`
        );
        return this._$q.when([]);
    }

    getAlarms(identifierGuidList: string[]): angular.IPromise<AlarmList> {
        if (identifierGuidList) {
            return this._alarmService.getList(identifierGuidList).then((data) => {
                return data;
            });
        }
        return this._$q.when({});
    }

    selectItem(item: Row) {
        this.selectedItem = item;
    }

    getAlarmParameter(item: Row, parameter: string): string {
        this.hasMultipleAlarmsWithSameSeverity = false;
        let value: string = '';
        if (item && parameter && this.alarmDefinitions) {
            const alarmForIdentifier = this.alarmDefinitions[item.identifierGuid];
            const currentAlarmDefinition = alarmForIdentifier.filter((a) => a.level === item.level);
            if (currentAlarmDefinition && currentAlarmDefinition.length === 1) {
                value = currentAlarmDefinition[0][parameter];
            } else if (currentAlarmDefinition.length > 1) {
                this.hasMultipleAlarmsWithSameSeverity = true;
            }
        }
        return value;
    }

    private addParametersToAlarms(items: Row[], alarmDefinitions: AlarmList): Row[] {
        if (items && alarmDefinitions) {
            items.forEach((i) => {
                const alarmForIdentifier = this.alarmDefinitions[i.identifierGuid];
                const currentAlarmDefinition = alarmForIdentifier.filter((a) => a.level === i.level);
                if (currentAlarmDefinition && currentAlarmDefinition.length === 1) {
                    i.parameters = currentAlarmDefinition[0];
                    this.addMissingParameters(i.parameters);
                }
            });
        }
        return items;
    }

    private addMissingParameters(parameters: Alarm) {
        if (!parameters.unitName) {
            parameters.unitName = undefined;
        }
        if (!parameters.high || parameters.isManual) {
            parameters.high = undefined;
        }
        if (!parameters.low || parameters.isManual) {
            parameters.low = undefined;
        }
    }

    private getCsvFormatConfiguration(widgetConfiguration: AlarmsTableConfiguration): { configuration: CsvFormatConfiguration; nbOfColumn: number } {
        let nbOfColumn: number = 0;
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        const configuration: CsvFormatConfiguration = {
            fields: {
                level: {
                    headerText: 'Level',
                    customValues: this.items.map((i) => this._levelScale.domain()[this._levelScale.range().indexOf(i.level)] + ' ' + i.type)
                },
                group_uniqueName: { showProperty: 'showGroup', headerText: 'Group' },
                identifierName: { showProperty: 'showIdentifier', headerText: 'Identifier' },
                tagName: { showProperty: 'showTag', headerText: 'Tag' },
                unitName: { showProperty: 'showUnit', headerText: 'Unit' },
                shortComment: { showProperty: 'showComment', headerText: 'Comment' },
                numberOfExcursions: {
                    showProperty: 'showNumberOfExcursions',
                    headerText: 'Number of excursions',
                    formatter: (value: number) => value.toString()
                },
                excursionTime: {
                    showProperty: 'showExcursionTime',
                    headerText: 'Total excursion time (h)',
                    formatter: (value: number) => formatValueForCsv(value, formatter)
                },
                maximumDeviation: {
                    showProperty: 'showMaxDeviation',
                    headerText: 'Max. deviation',
                    formatter: (value: number) => formatValueForCsv(value, formatter)
                },
                lastValue: {
                    showProperty: 'showCurrentValue',
                    headerText: 'Current value',
                    formatter: (value: number) => formatValueForCsv(value, formatter)
                },
                parameters_low: {
                    showProperty: 'showLow',
                    headerText: 'Low',
                    formatter: (value: number) => formatValueForCsv(value, formatter)
                },
                parameters_high: {
                    showProperty: 'showHigh',
                    headerText: 'High',
                    formatter: (value: number) => formatValueForCsv(value, formatter)
                },
                parameters_weight: {
                    showProperty: 'showWeight',
                    headerText: 'Weight',
                    formatter: (value: number) => formatValueForCsv(value, formatter)
                },
                parameters_probabilityOfFailure: { showProperty: 'showProbabilityOfFailure', headerText: 'ProbabilityOfFailure' },
                parameters_consequenceOfFailure: { showProperty: 'showConsequenceOfFailure', headerText: 'ConsequenceOfFailure' },
                parameters_comment: { showProperty: 'showAction', headerText: 'Action' },
                parameters_categories: {
                    showProperty: 'showCategories',
                    headerText: 'Categories',
                    formatter: (value: string[]) => value.join(', ')
                }
            },
            excludedFields: [
                '$$hashKey',
                'alarmValues',
                'categories',
                'domain',
                'enumerable',
                'group',
                'identifier',
                'identifierGuid',
                'parameters',
                'path',
                'ranges',
                'tagDefinitionGuid',
                'type',
                'valueType',
                'yDomain',
                'ys'
            ]
        };
        if (widgetConfiguration) {
            if (widgetConfiguration.showIdentifier === undefined) {
                widgetConfiguration.showIdentifier = true;
            }
            if (configuration?.fields) {
                Object.keys(configuration?.fields).forEach((property) => {
                    const showPropertyName = configuration.fields[property].showProperty;
                    const isPropertyVisible =
                        !showPropertyName ||
                        (showPropertyName in widgetConfiguration && widgetConfiguration[showPropertyName]) ||
                        (widgetConfiguration.parameters &&
                            showPropertyName in widgetConfiguration.parameters &&
                            widgetConfiguration.parameters[showPropertyName]);
                    if (isPropertyVisible) {
                        nbOfColumn++;
                    } else {
                        delete configuration.fields[property];
                        configuration.excludedFields.push(property);
                    }
                });
            }
            if (widgetConfiguration.showTrend) {
                nbOfColumn++;
            }
        }
        return { configuration, nbOfColumn };
    }
}

export const wtAlarmsTable: ComponentDefinition = {
    componentId: 'wtAlarmsTable',
    controller: AlarmsTableController,
    template: require('./alarmsTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<?',
        clicked: '=?',
        isEditMode: '<?',
        item: '<',
        ratio: '<?'
    }
};
